import {
	ChevronRight,
	LayoutDashboard,
	type LucideIcon,
	MessageCircle,
	ShieldAlert,
} from "lucide-react"
import type { ReactNode } from "react"
import { useSessionRole } from "@/entities/session"
import { useHaptic } from "@/shared/hooks/use-haptic"
import { openExternalLink } from "@/shared/lib/open-external-link"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/shared/ui/accordion"
import { HapticLink } from "@/shared/ui/haptic-link"
import { PageTransition } from "@/shared/ui/page-transition"
import { SectionHeading } from "@/shared/ui/section-heading"

const SUPPORT_USERNAME = "kmkadmin"

// Второй Mini App того же бота: бэкофис пускает по подписи Telegram без пароля.
const BACKOFFICE_MINI_APP_URL = "https://t.me/kmk_nav_bot/backoffice"

const forbidden = [
	"Копировать тексты, записывать уроки и выкладывать записи в открытый доступ",
	"Рассылать спам участникам канала",
	"Неуважительно и грубо вести себя с участниками, со мной и с гостями канала",
]

const faqItems = [
	{
		question: "Как стать участником сообщества?",
		answer:
			"Чтобы стать участником сообщества, зарегистрируйтесь на нашем сайте и оформите подписку.",
	},
	{
		question: "Какая аудитория в сообществе?",
		answer: "Наша аудитория это активные и заинтересованные люди, стремящиеся к развитию.",
	},
	{
		question: "Что я получу от участия в сообществе?",
		answer:
			"Вы получите доступ к эксклюзивному контенту, мероприятиям и сообществу единомышленников.",
	},
	{
		question: "Как изменить способ оплаты или отменить подписку?",
		answer: "Перейдите в настройки профиля, чтобы изменить способ оплаты или отменить подписку.",
	},
	{
		question: "Я живу не в Москве, могу ли я присоединиться к сообществу?",
		answer: "Да, участие доступно независимо от города проживания: всё онлайн.",
	},
	{
		question: "Можно ли подарить подписку?",
		answer: "Да, вы можете оформить подарочную подписку для другого пользователя.",
	},
	{
		question: "Я не нашёл ответа на вопрос, как мне связаться с вами?",
		answer: "Напишите нам через форму обратной связи или в поддержку.",
	},
]

const CARD = "rounded-2xl bg-card p-5 ring-1 ring-white/10"
const ROW =
	"flex w-full items-center gap-3 rounded-2xl bg-card p-4 text-left ring-1 ring-white/10 transition-transform duration-150 ease-[var(--ease-out-soft)] active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"

function ActionRow({
	icon: Icon,
	title,
	description,
	onClick,
	to,
}: {
	icon: LucideIcon
	title: string
	description: string
	onClick?: () => void
	to?: string
}) {
	const { triggerHaptic } = useHaptic()
	const body: ReactNode = (
		<>
			<span className="grid size-10 shrink-0 place-items-center rounded-xl bg-primary/15 text-primary">
				<Icon className="size-5" />
			</span>
			<span className="min-w-0 flex-1">
				<span className="block text-[15px] leading-tight font-semibold">{title}</span>
				<span className="mt-0.5 block text-xs leading-snug text-muted-foreground">
					{description}
				</span>
			</span>
			<ChevronRight className="size-4 shrink-0 text-muted-foreground" />
		</>
	)

	if (to) {
		return (
			<HapticLink to={to} className={ROW}>
				{body}
			</HapticLink>
		)
	}

	return (
		<button
			type="button"
			className={ROW}
			onClick={() => {
				triggerHaptic("light")
				onClick?.()
			}}
		>
			{body}
		</button>
	)
}

export function AboutPage() {
	const openSupport = () => openExternalLink(`https://t.me/${SUPPORT_USERNAME}`)
	const { isModerator } = useSessionRole()

	return (
		<PageTransition>
			<header className="relative isolate -mt-[var(--app-body-padding-top)] pt-[var(--app-body-padding-top)]">
				<div
					aria-hidden
					className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-64 bg-[radial-gradient(120%_70%_at_50%_0%,color-mix(in_oklab,var(--color-primary)_24%,transparent),transparent_70%)]"
				/>
				<div className="container mx-auto max-w-3xl px-3 pt-5 pb-6 sm:px-4">
					<h1 className="text-3xl font-bold tracking-tight">Сообщество</h1>
					<p className="mt-2 text-sm leading-snug text-muted-foreground">
						Правила, ответы на частые вопросы и связь с нами.
					</p>
				</div>
			</header>

			<div className="container mx-auto flex max-w-3xl flex-col gap-8 px-3 pb-6 sm:px-4 lg:pb-8">
				{isModerator ? (
					<section>
						<SectionHeading title="Для команды" />
						<ActionRow
							icon={LayoutDashboard}
							title="Бэкофис"
							description="Посты, боты, рассылки и аналитика"
							onClick={() => openExternalLink(BACKOFFICE_MINI_APP_URL)}
						/>
					</section>
				) : null}

				<section>
					<SectionHeading title="Правила" />
					<div className={CARD}>
						<p className="text-sm font-semibold">Запрещено</p>
						<ul className="mt-2 space-y-2">
							{forbidden.map((rule) => (
								<li key={rule} className="flex gap-2.5 text-sm leading-snug text-muted-foreground">
									<span aria-hidden className="mt-2 size-1.5 shrink-0 rounded-full bg-primary" />
									{rule}
								</li>
							))}
						</ul>
						<p className="mt-4 text-sm leading-snug text-muted-foreground">
							При нарушении служба заботы напишет вам предупреждение в личные сообщения. Если
							ситуация повторится, мы будем вынуждены удалить вас из канала и аннулировать подписку.
						</p>
						<p className="mt-3 text-sm leading-snug">
							Цель канала в том, чтобы собрать сильное честное сообщество, где вы общаетесь,
							делитесь опытом и получаете свежие знания для своего роста.
						</p>
					</div>
				</section>

				<section>
					<SectionHeading title="Вопросы и ответы" />
					<div className="overflow-hidden rounded-2xl bg-card px-5 ring-1 ring-white/10">
						<Accordion type="single" collapsible className="w-full">
							{faqItems.map((item) => (
								<AccordionItem
									key={item.question}
									value={item.question}
									className="border-white/10 last:border-b-0"
								>
									<AccordionTrigger className="gap-3 text-left hover:no-underline">
										{item.question}
									</AccordionTrigger>
									<AccordionContent className="text-sm leading-snug text-muted-foreground">
										{item.answer}
									</AccordionContent>
								</AccordionItem>
							))}
						</Accordion>
					</div>
				</section>

				<section>
					<SectionHeading title="Поддержка" />
					<div className="flex flex-col gap-2.5">
						<ActionRow
							icon={MessageCircle}
							title="Написать в поддержку"
							description="Ответим в Telegram"
							onClick={openSupport}
						/>
						<ActionRow
							icon={ShieldAlert}
							title="Сообщить о нарушении"
							description="Спам в личке, реклама, оскорбления"
							onClick={openSupport}
						/>
					</div>
				</section>
			</div>
		</PageTransition>
	)
}
